<template>
  <base-layout>
    <base-dashboard :is-loading="isLoading">
      <div class="h-100 overflow-y-auto overflow-x-hidden d-flex flex-column">
        <div
          class="page-header bg-secondary m-0 p-3 border-bottom"
        >
          <h1 class="page-title">
            Campaign Matches - {{ campaign.name }}
          </h1>
        </div>
        <hr class="my-1">
        <div
          class="py-4 px-4"
        >
          <base-help-tooltip>
            <template v-slot:left>
              <div>
                <h3 class="page-second-title">
                  Daily Matches
                </h3>
              </div>
            </template>
            <template v-slot:popover-body>
              <p>See an overview of the campaign's matches. Use the filters to find matches broken down by publisher article, creative size, and advertiser article.</p>
            </template>
          </base-help-tooltip>
        </div>
        <div class="px-4 pb-4">
          <campaign-matches-filters
            v-if="filtersAreReadyToBeDisplayed"
            :items="items"
            :selected-items="selectedMatches"
            @filterItems="filterItems"
            @resetItems="resetItems"
            @searchItems="searchItems"
            @exportCsvFile="exportCsvFile"
          />
        </div>
        <div
          class="table-responsive pt-4"
        >
          <b-table
            class="card-table table-vcenter text-nowrap"
            tbody-tr-class="content-matches-table-row"
            :striped="true"
            :bordered="true"
            :hover="true"
            :items="items"
            :fields="tableHeaders"
            :sort-by.sync="sortTableBy"
            :sort-desc.sync="sortTableDesc"
            :per-page="perPageSelected"
            :current-page="currentPage"
            @sort-changed="resetPaging"
            data-test="content-matches-table"
            show-empty
          >
            <template v-slot:cell(selected)="{item}">
              <div class="selected-checkbox">
                <b-form-checkbox
                  v-model="item.selected"
                />
              </div>
            </template>
            <template
              v-slot:cell(score)="{item}"
            >
              {{ item.matchScore }}
            </template>
            <template
              v-slot:cell(publisherName)="{item}"
            >
              {{ item.publisherName }}
            </template>
            <template
              v-slot:cell(publisherHeadline)="{item}"
            >
              <a
                :href="item.publisherUrl"
                target="_blank"
              > {{ item.publisherHeadline }}</a>
            </template>
            <template
              v-slot:cell(advertiserContent)="{item}"
            >
              <a
                :href="item.advertiserContentUrl"
                target="_blank"
              >
                {{ item.advertiserContent }}
              </a>
            </template>
            <template
              v-slot:cell(creativeSize)="{item}"
            >
              <a
                :href="item.contentLink"
                target="_blank"
              >
                {{ item.creativeSize }}
              </a>
            </template>
            <template v-slot:cell(feedback)="{item}">
              <span class="icons-wrapper">
                <a
                  class="good-match icon"
                  :class="{ 'disabled': item.badMatchSelected }"
                  @click="updateMatch(item, 'GOOD')"
                ><i
                  class="success fa-thumbs-up"
                  :class="item.goodMatchSelected ? 'fa-solid' : 'fa-light'"
                /></a>
                <a
                  class="bad-match icon"
                  :class="{ 'disabled': item.goodMatchSelected }"
                  @click="updateMatch(item, 'BAD')"
                ><i
                  class="error fa-thumbs-down"
                  :class="item.badMatchSelected ? 'fa-solid' : 'fa-light'"
                /></a>
                <a
                  class="shield-match icon"
                  @click="flagMissingClassifiers(item.uuid)"
                ><i
                  class="info fa-dumbbell"
                  :class="item.shieldMatchSelected ? 'fa-solid' : 'fa-light'"
                /></a>
              </span>
              <brandsafety-classifiers-modal
                :classifiers="brandsafetyClassifiers"
                :item="item"
                @updateClassifiers="updateClassifiers"
              />
            </template>
            <template v-slot:empty>
              <b-alert
                show
                variant="warning"
                class="w-50 mx-auto text-center mb-0"
                v-if="!isLoading"
              >
                Sorry, there doesn't seem to be any matches for your search.
              </b-alert>
            </template>
          </b-table>
        </div>
        <div class="pt-4">
          <base-pagination
            data-test="campaign-matches-pagination"
            :items-count="items.length"
            :current-page.sync="currentPage"
            :per-page-selected.sync="perPageSelected"
            :page-number-options="perPageOptions"
            items-name="matches"
            @pagingUpdate="updatePageOptions"
          />
        </div>
      </div>
    </base-dashboard>
  </base-layout>
</template>
<script>

import { mapActions, mapMutations, mapState } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as campaignMatchesTypes from '@/modules/campaignMatches/store/types';
import * as contentTypes from '@/modules/content/store/types';
import BasePagination from '@/components/BasePagination.vue';
import BaseLayout from '@/components/BaseLayout.vue';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import BrandsafetyClassifiersModal from '@/views/CampaignMatches/BrandsafetyClassifiersModal.vue';
import BaseDashboard from '@/components/BaseDashboard.vue';
import { v4 as generateUuid } from 'uuid';
import textHelper from '@/core/mixins/textHelper';
import * as types from '@/store/types';
import fileHelper from '@/core/mixins/fileHelper';
import CampaignMatchesFilters from '@/views/CampaignMatches/CampaignMatchesFilters.vue';

export default {
  name: 'CampaignMatches',
  mixins: [
    fileHelper,
    textHelper,
  ],
  components: {
    CampaignMatchesFilters,
    BaseDashboard,
    BrandsafetyClassifiersModal,
    BaseHelpTooltip,
    BaseLayout,
    BasePagination,
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    selectedMatches() {
      return this.items.filter((item) => item.selected);
    },
    tableHeaders() {
      return [
        { key: 'selected', label: 'SELECT', tdClass: 'text-center' },
        { key: 'matchScore', label: 'MATCH SCORE', sortable: true },
        { key: 'publisherName', label: 'PUBLISHER NAME', sortable: true },
        { key: 'publisherHeadline', label: 'PUBLISHER CONTENT', sortable: true },
        { key: 'advertiserContent', label: 'ADVERTISER CONTENT', sortable: true },
        { key: 'creativeSize', label: 'CREATIVE SIZE', sortable: true },
        { key: 'feedback', label: 'FEEDBACK', tdClass: 'text-center' },
      ];
    },
  },
  created() {
    const { advertiserId, campaignId } = this.$route.params;
    this.GET_CAMPAIGN_BY_ID(campaignId);
    this.fetchDailyMatchesRelatedData(advertiserId, campaignId);
    this.FETCH_BRANDSAFETY_CLASSIFIERS().then((response) => {
      this.brandsafetyClassifiers = response.filter((classifier) => classifier !== 'NOTBLACKLISTED').sort();
    });
  },
  data() {
    return {
      currentPage: 1,
      perPageSelected: 30,
      perPageOptions: [30, 50, 100],
      isLoading: false,
      sortTableBy: 'matchScore',
      sortTableDesc: true,
      matchScoreData: [],
      items: [],
      brandsafetyClassifiers: [],
      filtersAreReadyToBeDisplayed: false,
    };
  },
  methods: {
    ...mapActions('campaigns', [
      campaignTypes.GET_CAMPAIGN_BY_ID,
    ]),
    ...mapActions('campaignMatches', [
      campaignMatchesTypes.FETCH_DAILY_MATCHES,
      campaignMatchesTypes.FETCH_BRANDSAFETY_CLASSIFIERS,
      campaignMatchesTypes.UPDATE_MATCHES,
      campaignMatchesTypes.UPDATE_BRANDSAFETY_CLASSIFIERS,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_EXISTING_CREATIVES,
      contentTypes.FETCH_CREATIVE_TEMPLATE_BY_ID,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    convertResponseRowsToTableRecords(rowsData, creativeIdsToCreativeMap) {
      this.matchScoreData = rowsData.map((row) => ({
        selected: false,
        uuid: generateUuid(),
        matchScore: row[0],
        publisherName: row[6],
        publisherUrl: row[2],
        publisherHeadline: row[1],
        publisherContentExternalId: row[8],
        advertiserContent: row[3],
        advertiserContentUrl: row[4],
        advertiserContentExternalId: row[9],
        creativeSize: creativeIdsToCreativeMap.get(Number(row[7]))?.sizeName,
        publisherCode: row[10],
        creativeId: Number(row[7]),
        creativeKey: creativeIdsToCreativeMap.get(Number(row[7]))?.key,
        goodMatchSelected: false,
        badMatchSelected: false,
        shieldMatchSelected: false,
      })).map((matchScoreData) => {
        matchScoreData.contentLink = `https://s3-eu-west-1.amazonaws.com/smartmatch-test/programmatic-tester/programmatictester.html?creativeId=${matchScoreData.creativeId}&creativeKey=${matchScoreData.creativeKey}&publisherPageUrl=${matchScoreData.publisherUrl}&advertiserContentId=${matchScoreData.advertiserContentExternalId}&publisherCode=${matchScoreData.publisherCode}&env=${process.env.NODE_ENV}`;
        return matchScoreData;
      });
      this.items = this.matchScoreData;
    },
    exportCsvFile() {
      if (this.selectedMatches.length > 0) {
        const fileName = `Daily Matches - ${this.campaign.name}.csv`;
        const csvRows = this.selectedMatches.map((item) => `${item.advertiserContent},${item.creativeName},${item.contentLink},${item.publisherUrl}`).join('\n');
        const csvContent = `Title, Creative Name, Content Preview, Publisher Content\n${csvRows}`;
        this.downloadCsvFile(csvContent, fileName);
        this.resetSelection();
      }
    },
    fetchDailyMatchesRelatedData(advertiserId, campaignId) {
      this.isLoading = true;
      Promise.all([this.FETCH_DAILY_MATCHES(campaignId), this.FETCH_EXISTING_CREATIVES({
        advertiserId,
        campaignId,
      })]).then(([tsvResponse, creativesResponse]) => {
        this.preparePage(tsvResponse, creativesResponse.creatives);
        this.isLoading = false;
      }).catch(() => {
        this.preparePage([]);
        this.isLoading = false;
      });
    },
    filterItems(filterOptions) {
      const { search } = filterOptions;
      this.items = !search ? this.items : this.items.filter((item) => search.includes(item[filterOptions.itemFieldName]));
    },
    flagMissingClassifiers(uuid) {
      this.$root.$emit('bv::show::modal', `FlagMissingClassifiers-${uuid}`);
    },
    populateTable(tsvResponse, creativeIdsToCreativeMap) {
      const rows = tsvResponse.split('\n')
        .filter((row) => row.trim().length > 0)
        .map((row) => row.split('\t'));
      this.convertResponseRowsToTableRecords(rows, creativeIdsToCreativeMap);
    },
    async preparePage(tsvResponse, creatives) {
      let templateIdsToSizeNames;
      await Promise.all(creatives.map((creative) => this.FETCH_CREATIVE_TEMPLATE_BY_ID(creative.creativeTemplateId)))
        .then((templateResponses) => {
          templateIdsToSizeNames = new Map(templateResponses.map((template) => [template.id, `${template.width}x${template.height}`]));
        });
      const creativeIdsToCreativeMap = new Map(creatives.map((creative) => [creative.id, {
        id: creative.id,
        key: creative.creativeKey,
        sizeName: templateIdsToSizeNames.get(creative.creativeTemplateId),
      }]));
      this.populateTable(tsvResponse, creativeIdsToCreativeMap);
      this.filtersAreReadyToBeDisplayed = true;
    },
    resetItems(resetOptions) {
      this.resetPaging();
      this.items = this.matchScoreData;
      this.searchItems(resetOptions.searchText);
      this.filterItems(resetOptions.filterOptions.advertiser);
      this.filterItems(resetOptions.filterOptions.publisher);
      this.filterItems(resetOptions.filterOptions.creative);
    },
    resetSelection() {
      this.selectedMatches.forEach((item) => {
        item.selected = false;
      });
    },
    resetPaging() {
      this.currentPage = 1;
    },
    searchItems(searchText) {
      this.items = !searchText ? this.items : this.items.filter((item) => item.publisherHeadline.toLowerCase().includes(searchText.toLowerCase()));
    },
    updateMatch(item, type) {
      if (item.goodMatchSelected || item.badMatchSelected) return;
      item[`${type.toLowerCase()}MatchSelected`] = true;
      this.UPDATE_MATCHES({
        advertiserContentExternalId: item.advertiserContentExternalId,
        publisherContentExternalId: item.publisherContentExternalId,
        label: type,
      }).catch(() => {
        item[`${type.toLowerCase()}MatchSelected`] = false;
        this.SET_GLOBAL_MESSAGE({
          message: `${this.capitalizeFirstLetter(type)} Match has not been updated due to the internal issue`,
          type: 'danger',
        });
      });
    },
    updateClassifiers(parameters) {
      const { item, labels } = parameters;
      item.shieldMatchSelected = true;
      this.UPDATE_BRANDSAFETY_CLASSIFIERS({
        advertiserContentExternalId: item.advertiserContentExternalId,
        publisherContentExternalId: item.publisherContentExternalId,
        labels,
      }).catch(() => {
        item.shieldMatchSelected = false;
        this.SET_GLOBAL_MESSAGE({
          message: 'Brandsafety classifiers have not been updated due to the internal issue',
          type: 'danger',
        });
      });
    },
    updatePageOptions(pageOptions) {
      this.page = pageOptions.page;
      this.perPageSelected = pageOptions.perPageSelected;
    },
  },
};
</script>
<style scoped lang="scss">
a:visited {
  color: #4300ff;
  text-decoration: underline;
  background-color: transparent;
}
::v-deep {
  .card-table thead tr th,
  .card-table tbody tr td {
    min-width: unset;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 230px;
  }
  .icon {
    padding: 10px;
  }
  .icons-wrapper {
    padding: 20px;
  }
  .fa-solid, .disabled {
    cursor: not-allowed;
  }
  .filters-row {
    display: flex;
    align-items: flex-end;
  }
}
</style>
